// Proposed 12-team bracket shown on the site: real teams, real times.
// Reuses the live bracket engine, then fills in team names and applies the
// custom Saturday running order worked out with the captains' preferences.
import { PLAY_DAYS } from './season3'
import { buildBracket, BO3_MINUTES, BO5_MINUTES } from './bracket'

// 12 teams. M & M slots in at seed 10, between Make HOTS Okay Again and Heavy
// Hitterz, pushing Heavy Hitterz to 11 and Team Calamity to 12.
export const SEEDING = [
  'Chungus Maximus',       // 1
  'Stormblessed',          // 2
  'Aiming For Third',      // 3
  'Skibidi Exodia',        // 4  — wanted earliest
  'NA Noobs + EU Dudes',   // 5
  'Piper Ken, 4 Men',      // 6
  'EG',                    // 7
  'Lets Get Fizzicle',     // 8
  'Make HOTS Okay Again',  // 9
  'M & M',                 // 10 — new team
  'Heavy Hitterz',         // 11 — can't play late; scheduled early
  'Team Calamity',         // 12
]

// Custom running order per play-day index (0 = Sat Oct 10 … 3 = Sun Oct 18), by
// match number. A nested array is a concurrent group (same start time, on
// separate streams).
// - Day 1 holds every Upper Round 1 & 2 match. Two concurrent pairs run the four
//   Upper Round 1 games back-to-back: M4 (Piper Ken vs Heavy Hitterz) ∥ M2 (NA
//   Noobs vs Team Calamity) at 10:00, then M1 (fizzicle vs make hots) ∥ M3 (EG vs
//   M & M) at 11:00. The four Upper Round 2 games then run one at a time. Heavy
//   Hitterz can't play late, so its follow-up (M8) leads off at 12:00 — both of
//   their games land in the morning. Every Round 2 team gets at least an hour
//   between matches; the day closes at 3:00 (last start).
// - Day 2: lower-bracket rounds 1 & 2. The two concurrent pairs open the day —
//   M9 ∥ M10 at 10:00, M11 ∥ M12 at 11:00 — then the Bo5 round-2 matches follow:
//   M13 at 12:00, M14 at 2:00.
// - Day 3: upper round 3 then lower round 3, all Bo5 and played one at a time —
//   M16 (Stormblessed vs Piper Ken) at 10:00, M15 (Chungus vs Skibidi) at 12:00,
//   M17 (Aiming For Third vs loser of M15) at 2:00, M18 (NA Noobs vs loser of
//   M16) at 4:00.
// - Day 4: the Upper Final (M20) and Lower Round 4 (M19) run concurrently at
//   10:00, then the Lower Final (M21) at 12:00 and the Grand Final (M22) at 2:00.
// Every match from Upper Round 3 on is a 2-hour Bo5; Day 4 closes at 4:00.
const DAY_ORDERS = {
  0: [[4, 2], [1, 3], 8, 6, 7, 5],
  1: [[9, 10], [11, 12], 13, 14],
  2: [16, 15, 17, 18],
  3: [[19, 20], 21, 22],
}

// Match numbers played at a non-default best-of for this event (overrides the
// engine's Bo3/Bo5 assignment). Lower Round 2 (M13, M14) and everything from
// Upper Round 3 onward (M15–M19; M20–M22 are already Bo5) are run as Bo5.
const BEST_OF_OVERRIDES = {
  13: 5, 14: 5, 15: 5, 16: 5, 17: 5, 18: 5, 19: 5, 20: 5,
}

// Completed-match results, by match number: the value is the winner. The loser
// is whichever participant isn't the winner. Any "Winner of M#" / "Loser of M#"
// reference resolves to a real team name once its feeding match has a result
// here; matches not yet played stay as placeholders.
const RESULTS = {
  1: 'Lets Get Fizzicle',    // beat Make HOTS Okay Again
  2: 'NA Noobs + EU Dudes',  // beat Team Calamity
  3: 'EG',                   // beat M & M
  4: 'Piper Ken, 4 Men',     // beat Heavy Hitterz
  5: 'Chungus Maximus',      // beat Lets Get Fizzicle
  6: 'Skibidi Exodia',       // beat NA Noobs + EU Dudes
  7: 'Stormblessed',         // beat EG
  8: 'Piper Ken, 4 Men',     // beat Aiming For Third
  // Day 2 (lower bracket rounds 1 & 2)
  9: 'Aiming For Third',     // beat Make HOTS Okay Again (LR1)
  10: 'EG',                  // beat Team Calamity (LR1)
  11: 'NA Noobs + EU Dudes', // beat M & M (LR1)
  12: 'Lets Get Fizzicle',   // beat Heavy Hitterz (LR1)
  13: 'Aiming For Third',    // beat EG 3-1 (LR2, Bo5)
  14: 'NA Noobs + EU Dudes', // beat Lets Get Fizzicle 3-0 (LR2, Bo5)
  // Day 3 (upper round 3)
  15: 'Chungus Maximus',     // beat Skibidi Exodia (UR3, Bo5)
  16: 'Stormblessed',        // beat Piper Ken, 4 Men (UR3, Bo5)
  // Day 3 (lower round 3)
  17: 'Skibidi Exodia',      // beat Aiming For Third (LR3, Bo5)
  18: 'Piper Ken, 4 Men',    // beat NA Noobs + EU Dudes (LR3, Bo5)
}

// Resolve every match's participant labels into real team names, walking matches
// in number order so each "Winner/Loser of M#" reference is known before use.
const resolveNames = (bracket) => {
  const winner = {}
  const loser = {}
  const sub = (ref) =>
    ref
      .replace(/^Seed (\d+)$/, (_, n) => SEEDING[Number(n) - 1])
      .replace(/^Winner of M(\d+)$/, (ph, n) => winner[n] || ph)
      .replace(/^Loser of M(\d+)$/, (ph, n) => loser[n] || ph)
  ;[...bracket.matches]
    .sort((a, b) => a.number - b.number)
    .forEach((m) => {
      m.a = sub(m.a)
      m.b = sub(m.b)
      const w = RESULTS[m.number]
      if (w) {
        winner[m.number] = w
        loser[m.number] = m.a === w ? m.b : m.a
      }
    })
}

export const buildStaging = () => {
  const bracket = buildBracket(SEEDING.length, PLAY_DAYS)

  resolveNames(bracket)

  bracket.matches.forEach((m) => {
    if (BEST_OF_OVERRIDES[m.number]) {
      m.bestOf = BEST_OF_OVERRIDES[m.number]
      m.minutes = m.bestOf === 5 ? BO5_MINUTES : BO3_MINUTES
    }
  })

  // Apply each custom day order, re-timing matches back-to-back from that day's
  // start and reassigning their day. A concurrent group shares a start time and
  // advances the clock by its longest match.
  const byNumber = Object.fromEntries(bracket.matches.map((m) => [m.number, m]))
  Object.entries(DAY_ORDERS).forEach(([dayIndex, sequence]) => {
    const i = Number(dayIndex)
    const matches = []
    let cursor = new Date(PLAY_DAYS[i].start).getTime()
    sequence.forEach((entry) => {
      const group = (Array.isArray(entry) ? entry : [entry]).map((n) => byNumber[n]).filter(Boolean)
      if (!group.length) return
      const span = Math.max(...group.map((m) => m.minutes))
      group.forEach((m) => {
        m.day = i
        m.start = new Date(cursor)
        m.end = new Date(cursor + m.minutes * 60000)
        m.concurrent = group.length > 1
        matches.push(m)
      })
      cursor += span * 60000
    })
    bracket.days[i].matches = matches
    bracket.days[i].end = new Date(cursor)
  })

  return bracket
}
